import { FC, useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  findBy,
  makeStyles,
  MuiStack,
} from '@perk-ui/core'
import clsx from 'clsx'
import { useHistory, useRouteMatch } from 'react-router'

import { useCurrentSite } from '../../../components/SitesProvider'
import config from '../../../config/app-config'
import usePatientCases from '../../../features/query-hooks/usePatientCases'
import useSurveyResponseGroupsAlt from '../../../features/query-hooks/useSurveyResponseGroupsAlt'
import PatientSelfOnboardUrls, {
  PatientSelfOnboardParams,
} from '../PatientSelfOnboardUrls'

const useStyles = makeStyles(() => ({
  root: {
    maxWidth: '787px',
    width: '100%',
    height: '100%',
  },
  getStarted: {
    fontFamily: 'DM Sans',
    fontSize: '40px',
    fontWeight: 700,
    lineHeight: '40px',
    textAlign: 'center',
    marginBottom: '40px',
  },
  patientCard: {
    width: '262px',
    height: '240px',
    borderRadius: '17px',
    border: '2px',
    opacity: '0px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: '#FAFAFA',
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': { boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' },
  },
  avatar: {
    backgroundColor: '#E3F2FD',
    width: '80px',
    height: '80px',
    marginTop: '20px',
    marginBottom: '40px',
  },
  avatarIcon: {
    width: '40px',
    height: '40px',
    color: '#42A5F5',
  },
  cardText: {
    fontFamily: 'DM Sans',
    fontSize: '24px',
    fontWeight: 500,
    lineHeight: '40px',
    textAlign: 'center',
  },
  activeCard: {
    border: '2px solid #56AAE8',
    '& p': {
      color: '#56AAE8',
    },
  },
  iFrame: {
    height: 'calc(100dvh - 64px)',
  },
  centered: {
    width: '100%',
    textAlign: 'center',
  },
  hidden: {
    display: 'none',
  },
}))

interface SurveyProps {}

const Survey: FC<SurveyProps> = () => {
  const classes = useStyles()
  const { push } = useHistory()
  const siteOrganization = useCurrentSite().organization
  const hasCase = useRouteMatch<PatientSelfOnboardParams>(
    PatientSelfOnboardUrls.surveyCompletion.match,
  )
  const { patientId, caseId } = hasCase?.params || {}
  const { data: openSurveys = [], isLoading } = useSurveyResponseGroupsAlt({
    patientId: patientId,
    patientCaseId: caseId,
  })
  const [iframeLoaded, setIframeLoaded] = useState(false)
  const { data: patientCases = [] } = usePatientCases(patientId)
  const patientCase = findBy('id', caseId, patientCases)

  useEffect(() => {
    const listenerAction = (e: MessageEvent) => {
      if (e?.origin === config.patientUrl) {
        if (e.data === 'details') {
          push(
            PatientSelfOnboardUrls.existingPatientDetails.create(
              patientId || '',
            ),
          )
        } else if (e.data === 'home') {
          push(PatientSelfOnboardUrls.base.match)
        }
      }
    }
    window.addEventListener('message', listenerAction)
    return () => {
      window.removeEventListener('message', listenerAction)
    }
  }, [])

  const openSurvey = openSurveys[0]
  const treatmentProductManufacturer =
    patientCase?.patientTreatments?.[0]?.treatmentProduct?.manufacturer || ''

  return (
    <Box className={classes.root}>
      <MuiStack justifyContent="center">
        {!isLoading && !openSurvey && (
          <Button
            fullWidth
            onClick={() => {
              push(
                PatientSelfOnboardUrls.existingPatientDetails.create(
                  patientId || '',
                ),
              )
            }}
          >
            No surveys available
          </Button>
        )}
        {(isLoading || !iframeLoaded) && (
          <Box className={classes.centered} my={'30%'}>
            <CircularProgress />
          </Box>
        )}
        {!isLoading && openSurvey && openSurvey.shortLink && (
          <iframe
            className={clsx(classes.iFrame, {
              [classes.hidden]: !iframeLoaded,
            })}
            src={
              openSurvey.shortLink +
              `?comm_channel=manual&org=${siteOrganization.name}&manufacturer=${treatmentProductManufacturer}&source=embedded`
            }
            frameBorder="0"
            allowFullScreen
            onLoad={() => setIframeLoaded(true)}
            title={'Survey'}
          />
        )}
      </MuiStack>
    </Box>
  )
}
export default Survey
